<script setup>
import { ref, computed, onMounted } from 'vue';
import { 
  NButton, NEmpty, NSpin, NResult, NCard, NTime, NForm, NFormItem, 
  NSelect, NTimePicker, NInputNumber, NSwitch, NPopconfirm, NModal, 
  NSpace, NTabs, NTabPane, NTag, NDivider, NGrid, NGridItem, NInputGroup,
  NInput
} from 'naive-ui';
import { 
  fetchProviderSchedules, 
  createProviderSchedule, 
  updateProviderSchedule, 
  deleteProviderSchedule, 
  updateProviderWaitTime,
  getWeekDays,
  getDayName,
  formatTimeDisplay,
  normalizeTime,
  groupSchedulesByDay,
  sortSchedulesByDay
} from '@/services/schedule';
import { fetchProviderProfile } from '@/services/provider';
import { authToken, isAuthenticated, getCurrentUser } from '@/services/auth';

// State variables
const providerProfile = ref(null);
const schedules = ref([]);
const loading = ref(true);
const error = ref(null);
const currentUser = ref(null);
const isProvider = ref(false);

// Form state
const showFormModal = ref(false);
const formMode = ref('create'); // 'create' or 'edit'
const currentSchedule = ref(null);
const formData = ref({
  day_of_week: 1, // Monday
  start_time: '09:00',
  end_time: '17:00',
  is_closed: false,
  break_start: null,
  break_end: null,
  notes: ''
});

// Wait time modal state
const showWaitTimeModal = ref(false);
const newWaitTime = ref(0);

// Day tabs state
const activeTab = ref(1); // Start with Monday
const weekDays = getWeekDays();

// Computed properties
const schedulesGrouped = computed(() => {
  return groupSchedulesByDay(schedules.value);
});

const currentDaySchedules = computed(() => {
  return schedulesGrouped.value[activeTab.value];
});

// Load data when component mounts
onMounted(async () => {
  if (!isAuthenticated.value) {
    error.value = 'Please log in to manage schedules';
    loading.value = false;
    return;
  }
  
  currentUser.value = await getCurrentUser();
  await loadData();
});

// Function to load provider profile and schedules
async function loadData() {
  try {
    loading.value = true;
    error.value = null;
    
    // Load provider profile
    const profile = await fetchProviderProfile(authToken.value);
    providerProfile.value = profile;
    isProvider.value = !!profile;
    
    if (!profile) {
      error.value = 'You need to create a provider profile first';
      return;
    }
    
    // Load schedules
    const result = await fetchProviderSchedules(profile.id);
    
    if (result.success) {
      schedules.value = sortSchedulesByDay(result.schedules);
    } else {
      throw new Error(result.error || 'Failed to load schedules');
    }
  } catch (err) {
    console.error('Failed to load data:', err);
    error.value = 'Failed to load schedules. Please try again later.';
  } finally {
    loading.value = false;
  }
}

// Function to open form modal for creating a new schedule
function openCreateForm(dayOfWeek = null) {
  formMode.value = 'create';
  formData.value = {
    day_of_week: dayOfWeek ?? activeTab.value,
    start_time: '09:00',
    end_time: '17:00',
    is_closed: false,
    break_start: null,
    break_end: null,
    notes: ''
  };
  showFormModal.value = true;
}

// Function to open form modal for editing an existing schedule
function openEditForm(schedule) {
  formMode.value = 'edit';
  currentSchedule.value = schedule;
  
  // Remove seconds from times for TimePicker
  const formatTimeForPicker = (time) => {
    if (!time) return null;
    return time.substring(0, 5); // "HH:MM:SS" -> "HH:MM"
  };
  
  formData.value = {
    day_of_week: schedule.day_of_week,
    start_time: formatTimeForPicker(schedule.start_time),
    end_time: formatTimeForPicker(schedule.end_time),
    is_closed: schedule.is_closed,
    break_start: formatTimeForPicker(schedule.break_start),
    break_end: formatTimeForPicker(schedule.break_end),
    notes: schedule.notes || ''
  };
  showFormModal.value = true;
}

// Function to open wait time modal
function openWaitTimeModal() {
  newWaitTime.value = providerProfile.value?.avg_wait_time_minutes || 0;
  showWaitTimeModal.value = true;
}

// Function to save a schedule (create or update)
async function saveSchedule() {
  try {
    if (!providerProfile.value) return;
    
    // Normalize times to include seconds
    const scheduleData = {
      ...formData.value,
      start_time: normalizeTime(formData.value.start_time),
      end_time: normalizeTime(formData.value.end_time),
      break_start: formData.value.break_start ? normalizeTime(formData.value.break_start) : null,
      break_end: formData.value.break_end ? normalizeTime(formData.value.break_end) : null,
    };
    
    let result;
    
    if (formMode.value === 'create') {
      result = await createProviderSchedule(
        authToken.value, 
        providerProfile.value.id, 
        scheduleData
      );
      
      if (result.success) {
        schedules.value.push(result.schedule);
        schedules.value = sortSchedulesByDay(schedules.value);
        window.$message.success('Schedule created successfully');
      } else {
        throw new Error(result.error);
      }
    } else {
      result = await updateProviderSchedule(
        authToken.value,
        providerProfile.value.id,
        currentSchedule.value.id,
        scheduleData
      );
      
      if (result.success) {
        // Update the schedule in the array
        const index = schedules.value.findIndex(s => s.id === result.schedule.id);
        if (index !== -1) {
          schedules.value[index] = result.schedule;
        }
        window.$message.success('Schedule updated successfully');
      } else {
        throw new Error(result.error);
      }
    }
    
    showFormModal.value = false;
  } catch (err) {
    console.error('Failed to save schedule:', err);
    window.$message.error(err.message || `Failed to ${formMode.value} schedule. Please try again.`);
  }
}

// Function to update wait time (for entire provider, not per-schedule)
async function saveWaitTime() {
  try {
    if (!providerProfile.value) return;
    
    const result = await updateProviderWaitTime(
      authToken.value,
      providerProfile.value.id,
      newWaitTime.value
    );
    
    if (result.success) {
      // Update local provider profile
      providerProfile.value.avg_wait_time_minutes = newWaitTime.value;
      window.$message.success('Wait time updated successfully');
      showWaitTimeModal.value = false;
    } else {
      throw new Error(result.error);
    }
  } catch (err) {
    console.error('Failed to update wait time:', err);
    window.$message.error(err.message || 'Failed to update wait time. Please try again.');
  }
}

// Function to delete a schedule
async function deleteSchedule(scheduleId) {
  try {
    if (!providerProfile.value) return;
    
    const result = await deleteProviderSchedule(
      authToken.value, 
      providerProfile.value.id, 
      scheduleId
    );
    
    if (result.success) {
      // Remove the schedule from the array
      schedules.value = schedules.value.filter(s => s.id !== scheduleId);
      window.$message.success('Schedule deleted successfully');
    } else {
      throw new Error(result.error);
    }
  } catch (err) {
    console.error('Failed to delete schedule:', err);
    window.$message.error(err.message || 'Failed to delete schedule. Please try again.');
  }
}

// Toggle schedule closed status
async function toggleClosed(schedule) {
  try {
    if (!providerProfile.value) return;
    
    const result = await updateProviderSchedule(
      authToken.value,
      providerProfile.value.id,
      schedule.id,
      { is_closed: !schedule.is_closed }
    );
    
    if (result.success) {
      const index = schedules.value.findIndex(s => s.id === schedule.id);
      if (index !== -1) {
        schedules.value[index] = result.schedule;
      }
      window.$message.success(result.schedule.is_closed ? 'Marked as closed' : 'Marked as open');
    } else {
      throw new Error(result.error);
    }
  } catch (err) {
    console.error('Failed to toggle schedule:', err);
    window.$message.error(err.message || 'Failed to update schedule.');
  }
}
</script>

<template>
  <div class="schedules-container">
    <div class="schedules-header">
      <h1 class="text-2xl font-bold text-gray-800 mb-4">Provider Schedules</h1>
      <p class="text-gray-600 mb-4" v-if="providerProfile">
        Manage your availability schedule for {{ providerProfile.name }}.
      </p>
      
      <!-- Provider-wide wait time -->
      <div v-if="providerProfile" class="wait-time-banner mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm text-gray-600">Current Average Wait Time</p>
            <p class="text-2xl font-bold text-blue-600">
              {{ providerProfile.avg_wait_time_minutes || 0 }} minutes
            </p>
          </div>
          <NButton type="primary" @click="openWaitTimeModal">
            Update Wait Time
          </NButton>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <NSpin v-if="loading" class="flex justify-center py-12">
      <template #description>Loading your schedules...</template>
    </NSpin>

    <!-- Error state -->
    <NResult 
      v-else-if="error" 
      status="error" 
      title="Error Loading Schedules" 
      :description="error"
    >
      <template #footer>
        <NButton v-if="error.includes('create a provider profile')" @click="$router.push('/provider/create')">
          Create Provider Profile
        </NButton>
        <NButton v-else @click="loadData">Try Again</NButton>
      </template>
    </NResult>

    <!-- Main content -->
    <div v-else-if="providerProfile">
      <!-- Add button -->
      <div class="mb-6 flex justify-end">
        <NButton type="primary" @click="openCreateForm()">
          Add Schedule
        </NButton>
      </div>

      <!-- Day tabs -->
      <NTabs v-model:value="activeTab" type="line" animated>
        <NTabPane 
          v-for="day in weekDays" 
          :key="day.value" 
          :name="day.value" 
          :tab="day.label"
        >
          <!-- Empty state for day -->
          <NEmpty 
            v-if="!currentDaySchedules" 
            :description="`No schedule set for ${day.label}`"
          >
            <template #extra>
              <NButton size="small" @click="openCreateForm(day.value)">
                Add Schedule for {{ day.label }}
              </NButton>
            </template>
          </NEmpty>

          <!-- Schedule for day (only 1 per day) -->
          <NCard
            v-else
            :class="[
              'schedule-card',
              currentDaySchedules.is_closed ? 'border-l-red-500' : 'border-l-green-500'
            ]"
            :title="`${formatTimeDisplay(currentDaySchedules.start_time)} - ${formatTimeDisplay(currentDaySchedules.end_time)}`"
            :bordered="true"
          >
            <template #header-extra>
              <NTag :type="currentDaySchedules.is_closed ? 'error' : 'success'">
                {{ currentDaySchedules.is_closed ? 'Closed' : 'Open' }}
              </NTag>
            </template>
            
            <div class="schedule-details">
              <!-- Break time -->
              <div v-if="currentDaySchedules.break_start && currentDaySchedules.break_end" class="mb-3">
                <p class="text-sm text-gray-600">
                  <strong>Break:</strong> 
                  {{ formatTimeDisplay(currentDaySchedules.break_start) }} - 
                  {{ formatTimeDisplay(currentDaySchedules.break_end) }}
                </p>
              </div>
              
              <!-- Notes -->
              <div v-if="currentDaySchedules.notes" class="mb-3">
                <p class="text-sm text-gray-600">
                  <strong>Notes:</strong> {{ currentDaySchedules.notes }}
                </p>
              </div>
              
              <!-- Created/Updated info -->
              <div class="text-xs text-gray-400 mt-4">
                <p>Created: <NTime :time="new Date(currentDaySchedules.created_at)" format="MMM dd, yyyy" /></p>
                <p v-if="currentDaySchedules.updated_at">
                  Updated: <NTime :time="new Date(currentDaySchedules.updated_at)" format="MMM dd, yyyy" />
                </p>
              </div>
            </div>
            
            <template #footer>
              <div class="flex justify-between items-center">
                <NSwitch 
                  :value="!currentDaySchedules.is_closed" 
                  @update:value="toggleClosed(currentDaySchedules)"
                >
                  <template #checked>Open</template>
                  <template #unchecked>Closed</template>
                </NSwitch>
                
                <div class="flex space-x-2">
                  <NButton size="small" @click="openEditForm(currentDaySchedules)">
                    Edit
                  </NButton>
                  <NPopconfirm
                    @positive-click="deleteSchedule(currentDaySchedules.id)"
                    positive-text="Yes"
                    negative-text="No"
                  >
                    <template #trigger>
                      <NButton size="small" type="error">
                        Delete
                      </NButton>
                    </template>
                    Are you sure you want to delete this schedule?
                  </NPopconfirm>
                </div>
              </div>
            </template>
          </NCard>
        </NTabPane>
      </NTabs>
    </div>

    <!-- Schedule form modal -->
    <NModal 
      v-model:show="showFormModal" 
      preset="card" 
      :title="formMode === 'create' ? 'Add Schedule' : 'Edit Schedule'"
      style="max-width: 600px"
    >
      <NForm :model="formData" label-placement="left" label-width="120">
        <NFormItem label="Day" required>
          <NSelect
            v-model:value="formData.day_of_week"
            :options="weekDays"
            :disabled="formMode === 'edit'"
          />
        </NFormItem>

        <NFormItem label="Start Time" required>
          <NTimePicker
            v-model:formatted-value="formData.start_time"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="Select start time"
            :clearable="false"
            style="width: 100%"
          />
        </NFormItem>

        <NFormItem label="End Time" required>
          <NTimePicker
            v-model:formatted-value="formData.end_time"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="Select end time"
            :clearable="false"
            style="width: 100%"
          />
        </NFormItem>

        <NDivider>Break Time (Optional)</NDivider>

        <NFormItem label="Break Start">
          <NTimePicker
            v-model:formatted-value="formData.break_start"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="Select break start"
            :clearable="true"
            style="width: 100%"
          />
        </NFormItem>

        <NFormItem label="Break End">
          <NTimePicker
            v-model:formatted-value="formData.break_end"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="Select break end"
            :clearable="true"
            style="width: 100%"
          />
        </NFormItem>

        <NDivider />

        <NFormItem label="Closed">
          <NSwitch v-model:value="formData.is_closed" />
          <span class="ml-2 text-sm text-gray-500">
            (Mark as closed if you're not available this day)
          </span>
        </NFormItem>

        <NFormItem label="Notes">
          <NInput
            v-model:value="formData.notes"
            type="textarea"
            placeholder="Optional notes about this schedule"
            :autosize="{ minRows: 2, maxRows: 4 }"
          />
        </NFormItem>
      </NForm>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <NButton @click="showFormModal = false">
            Cancel
          </NButton>
          <NButton type="primary" @click="saveSchedule">
            {{ formMode === 'create' ? 'Create' : 'Update' }}
          </NButton>
        </div>
      </template>
    </NModal>

    <!-- Wait time modal (provider-wide) -->
    <NModal 
      v-model:show="showWaitTimeModal" 
      preset="card" 
      title="Update Average Wait Time"
      style="max-width: 500px"
    >
      <div class="wait-time-form">
        <p class="mb-4 text-gray-600">
          Update your average wait time. This applies to all your schedules and helps patients know what to expect.
        </p>
        
        <NFormItem label="Wait Time (minutes)">
          <NInputNumber
            v-model:value="newWaitTime"
            :min="0"
            :max="240"
            style="width: 100%"
          />
        </NFormItem>
        
        <div class="mt-4 p-3 bg-gray-50 rounded">
          <p class="text-sm text-gray-600">
            <strong>Current:</strong> {{ providerProfile?.avg_wait_time_minutes || 0 }} minutes<br/>
            <strong>New:</strong> {{ newWaitTime }} minutes
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <NButton @click="showWaitTimeModal = false">
            Cancel
          </NButton>
          <NButton type="primary" @click="saveWaitTime">
            Update
          </NButton>
        </div>
      </template>
    </NModal>
  </div>
</template>

<style scoped>
.schedules-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.schedule-card {
  transition: all 0.2s ease;
  border-left-width: 4px;
}

.schedule-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.schedule-details {
  display: flex;
  flex-direction: column;
}

.border-l-green-500 {
  border-left-color: #10b981;
}

.border-l-red-500 {
  border-left-color: #ef4444;
}

.wait-time-form {
  padding: 0 1rem;
}

.wait-time-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.wait-time-banner p {
  color: white !important;
}
</style>